/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: RecordingSelect.tsx
 🗓️ Created: 2025-01-XX
 🧩 Description:
    Reusable component for selecting voice recordings with audio playback.
    Can be used across multiple modules (Paging Groups, IVR, etc.)

 🔧 Notes:
    - Loads recordings from Recording Management module
    - Provides audio playback capability
    - Returns file_path or id based on returnType prop
    - Supports filtering by category and status
    - When TTS AI Profiles are available, shows buttons to
      create a new recording or edit the selected one via
      RecordingTtsModal

 🔁 Last Updated: 2026-03-15 by Rodrigo Cuadra
────────────────────────────────────────────────────────────────*/

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Play, Pause, Volume2, Bot, Pencil } from 'lucide-react';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from './select-shadcn';
import { Button } from './button';
import SimpleTooltip from './SimpleTooltip';
import RecordingTtsModal from './RecordingTtsModal';
import { buildApiUrl, getApiHeaders } from '../../config/api';
import { VoiceRecording, AiProfile } from '../../modules/recordingManagement/types';
import { useContextStore } from '../../store/context';

interface RecordingSelectProps {
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  returnType?: 'file_path' | 'id'; // What to return: file_path or id
  category?: 'announcement' | 'ivr' | 'voicemail' | 'feature_code' | 'custom'; // Filter by category
  showPlayButton?: boolean; // Show audio player
  hasError?: boolean; // Visual error state for styling
  showTtsButton?: boolean; // Show TTS/Upload create + edit buttons (auto-hidden if no TTS profiles)
  id?: string;
}

export default function RecordingSelect({
  value,
  onChange,
  placeholder = 'Select a recording...',
  className = '',
  disabled = false,
  returnType = 'file_path',
  category,
  showPlayButton = true,
  hasError = false,
  showTtsButton = true,
  id
}: RecordingSelectProps) {
  const { selectedTenant } = useContextStore();

  const [recordings, setRecordings] = useState<VoiceRecording[]>([]);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setError] = useState<string | null>(null);
  const [playingRecordingId, setPlayingRecordingId] = useState<number | null>(null);
  const [isPaused, setIsPaused] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // TTS-related state
  const [ttsProfiles, setTtsProfiles] = useState<AiProfile[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editRecordingId, setEditRecordingId] = useState<string | number | null>(null);

  // Load recordings with pagination (API max limit is 100)
  const loadRecordings = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      let allRecordings: VoiceRecording[] = [];
      let page = 1;
      const limit = 100; // API maximum
      let hasMore = true;

      // Load all recordings using pagination
      while (hasMore) {
        const queryParams = new URLSearchParams({
          page: page.toString(),
          limit: limit.toString(),
          sortBy: 'name',
          sortOrder: 'asc',
          status: 'active', // Only active recordings
          ...(category && { category }) // Filter by category if provided
        });

        const url = `${buildApiUrl('/api/telephony/recordings')}?${queryParams}`;
        
        const response = await fetch(url, {
          headers: getApiHeaders()
        });

        if (response.ok) {
          const result = await response.json();
          
          // API returns { data: [], pagination: {} } format
          let pageRecordings: VoiceRecording[] = [];
          if (result && Array.isArray(result.data)) {
            pageRecordings = result.data;
          }
          
          allRecordings = [...allRecordings, ...pageRecordings];
          
          // Check if there are more pages
          const totalPages = result.pagination?.totalPages || 1;
          hasMore = page < totalPages && pageRecordings.length === limit;
          page++;
        } else {
          const errorText = await response.text().catch(() => 'Unknown error');
          
          // Only show error if it's the first page
          if (page === 1) {
            let errorMessage = `Failed to load recordings (${response.status})`;
            if (response.status === 403) {
              errorMessage = 'You do not have permission to access recordings';
            } else if (response.status === 401) {
              errorMessage = 'Authentication required';
            } else if (response.status === 404) {
              errorMessage = 'Recordings endpoint not found';
            }
            setError(errorMessage);
          }
          break;
        }
      }
      
      setRecordings(allRecordings);
      
      // Set informational message if no recordings found (not an error, just informational)
      if (allRecordings.length === 0) {
        setError(null); // Clear any previous errors
      }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      setError(`Error loading recordings: ${error.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  }, [category]);

  // Load TTS AI profiles (only if showTtsButton is enabled)
  const loadTtsProfiles = useCallback(async () => {
    if (!showTtsButton) return;
    const tenantSlug = selectedTenant?.slug || 'main';
    try {
      const res = await fetch(buildApiUrl('/api/ai/profiles?type=tts&status=true'), {
        headers: getApiHeaders(tenantSlug),
      });
      if (!res.ok) return;
      const result = await res.json();
      setTtsProfiles(result.data || []);
    } catch {
      // silently ignore — button just won't appear
    }
  }, [showTtsButton, selectedTenant]);

  // Load recordings on mount
  useEffect(() => {
    loadRecordings();
    loadTtsProfiles();
  }, [loadRecordings, loadTtsProfiles]);

  // Get selected recording
  const selectedRecording = recordings.find(r => {
    if (!value || value === '' || value === '__none__') return false;
    if (returnType === 'file_path') {
      return r.file_path === value;
    } else {
      return r.id.toString() === value;
    }
  });

  // Play/Stop recording
  const handlePlayRecording = useCallback(async (recording: VoiceRecording) => {
    try {
      // If clicking on the same recording that's playing, toggle pause
      if (playingRecordingId === recording.id && audioRef.current) {
        if (isPaused) {
          await audioRef.current.play();
          setIsPaused(false);
        } else {
          audioRef.current.pause();
          setIsPaused(true);
        }
        return;
      }

      // Stop current audio if playing a different recording
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = '';
        audioRef.current = null;
      }

      const url = `${buildApiUrl(`/api/telephony/recordings/${recording.id}/download`)}?t=${Date.now()}`;
      const headers = getApiHeaders();
      
      const response = await fetch(url, {
        method: 'GET',
        headers
      });

      if (response.ok) {
        // Create audio element directly from blob URL
        const audioBlob = await response.blob();
        const audioUrl = URL.createObjectURL(audioBlob);
        
        // Create audio element and play
        const audio = document.createElement('audio');
        audio.src = audioUrl;
        
        // Store reference
        audioRef.current = audio;
        
        // Handle errors
        audio.onerror = () => {
          URL.revokeObjectURL(audioUrl);
          audioRef.current = null;
        };
        
        // Clean up on end
        audio.onended = () => {
          URL.revokeObjectURL(audioUrl);
          audio.remove();
          setPlayingRecordingId(null);
          setIsPaused(false);
          audioRef.current = null;
        };
        
        // Set state and play
        setPlayingRecordingId(recording.id);
        setIsPaused(false);
        await audio.play();
      }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      // Silently handle playback errors
    }
  }, [playingRecordingId, isPaused]);

  // Cleanup audio on unmount
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = '';
        audioRef.current = null;
      }
    };
  }, []);

  const handleValueChange = (newValue: string) => {
    if (onChange) {
      // '__none__' is the sentinel for the empty/None option
      onChange(newValue === '__none__' ? '' : newValue);
    }
  };

  // Called when user saves a recording from the modal
  const handleRecordingSaved = useCallback(async (recording: VoiceRecording) => {
    // Reload the list WITHOUT status filter so TTS recordings in 'processing'
    // state are immediately visible and selectable.
    setLoading(true);
    try {
      let allRecordings: VoiceRecording[] = [];
      let page = 1;
      const limit = 100;
      let hasMore = true;
      while (hasMore) {
        const queryParams = new URLSearchParams({
          page: page.toString(),
          limit: limit.toString(),
          sortBy: 'name',
          sortOrder: 'asc',
          ...(category && { category }),
        });
        const url = `${buildApiUrl('/api/telephony/recordings')}?${queryParams}`;
        const response = await fetch(url, { headers: getApiHeaders() });
        if (response.ok) {
          const result = await response.json();
          const pageRecordings: VoiceRecording[] = Array.isArray(result.data) ? result.data : [];
          allRecordings = [...allRecordings, ...pageRecordings];
          const totalPages = result.pagination?.totalPages || 1;
          hasMore = page < totalPages && pageRecordings.length === limit;
          page++;
        } else {
          break;
        }
      }
      setRecordings(allRecordings);
    } catch {
      // fallback — keep existing list
    } finally {
      setLoading(false);
    }

    // Auto-select the recording that was just created/updated
    const newValue = returnType === 'file_path' ? recording.file_path : recording.id.toString();
    if (newValue && onChange) {
      onChange(newValue);
    }
  }, [category, returnType, onChange]);

  const handleOpenCreate = useCallback(() => {
    setEditRecordingId(null);
    setModalOpen(true);
  }, []);

  const handleOpenEdit = useCallback(() => {
    if (!selectedRecording) return;
    setEditRecordingId(selectedRecording.id);
    setModalOpen(true);
  }, [selectedRecording]);

  const isCurrentlyPlaying = selectedRecording && playingRecordingId === selectedRecording.id;
  const isPausedForThis = isCurrentlyPlaying && isPaused;

  // Show TTS buttons only when there are TTS profiles available
  const hasTtsProfiles = ttsProfiles.length > 0;
  const showButtons = showTtsButton && hasTtsProfiles;

  // A value is "selected" if it's non-empty and not the sentinel
  const hasValue = Boolean(value && value !== '' && value !== '__none__');

  return (
    <>
      <div className={`flex items-center gap-2 ${className}`}>
        <div className="flex-1">
          <div className={disabled || loading ? 'opacity-50 pointer-events-none' : ''}>
            <Select
              value={value && value !== '' ? value : '__none__'}
              onValueChange={handleValueChange}
            >
              <SelectTrigger id={id} className={hasError ? 'border-red-500' : ''}>
                <SelectValue placeholder={loading ? 'Loading recordings...' : placeholder} />
              </SelectTrigger>
              <SelectContent>
              <SelectItem value="__none__">None</SelectItem>
              {loading && (
                <div className="px-3 py-2 text-sm text-muted-foreground">
                  Loading recordings...
                </div>
              )}
              {!loading && errorMessage && (
                <div className="px-3 py-2 text-sm text-red-500">
                  {errorMessage}
                </div>
              )}
              {!loading && !errorMessage && recordings.length === 0 && (
                <div className="px-3 py-2 text-sm text-muted-foreground">
                  {category 
                    ? `No active recordings found in category "${category}".`
                    : 'No active recordings available.'}
                  {showButtons && ' Use the + button to create one.'}
                </div>
              )}
              {recordings.map((recording) => (
                <SelectItem 
                  key={recording.id} 
                  value={returnType === 'file_path' ? recording.file_path : recording.id.toString()}
                >
                  {recording.name}
                  {recording.duration && (
                    <span className="text-xs text-muted-foreground ml-2">
                      ({recording.duration}s)
                    </span>
                  )}
                </SelectItem>
              ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Audio Player Button */}
        {showPlayButton && (
          <SimpleTooltip content={
            hasValue
              ? (isPausedForThis ? 'Resume' : isCurrentlyPlaying ? 'Pause' : 'Play')
              : 'Select a recording to play'
          }>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-10 w-10 p-0 flex-shrink-0"
              onClick={() => {
                if (selectedRecording) {
                  handlePlayRecording(selectedRecording);
                }
              }}
              disabled={!hasValue}
            >
              {hasValue ? (
                isPausedForThis ? (
                  <Play className="h-4 w-4" />
                ) : isCurrentlyPlaying ? (
                  <Pause className="h-4 w-4" />
                ) : (
                  <Volume2 className="h-4 w-4" />
                )
              ) : (
                <Volume2 className="h-4 w-4 opacity-50" />
              )}
            </Button>
          </SimpleTooltip>
        )}

        {/* Edit selected recording — visible whenever a value is selected */}
        {showButtons && hasValue && (
          <SimpleTooltip content="Edit this recording">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-10 w-10 p-0 flex-shrink-0 text-amber-600 border-amber-300 hover:bg-amber-50 hover:border-amber-400 dark:hover:bg-amber-950/30"
              onClick={() => {
                // Use selectedRecording.id if found, otherwise use value directly
                const idToEdit = selectedRecording ? selectedRecording.id : value;
                setEditRecordingId(idToEdit ?? null);
                setModalOpen(true);
              }}
              disabled={disabled}
            >
              <Pencil className="h-4 w-4" />
            </Button>
          </SimpleTooltip>
        )}

        {/* Create new recording via TTS / Upload */}
        {showButtons && (
          <SimpleTooltip content="Create new recording (TTS or upload)">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-10 w-10 p-0 flex-shrink-0 text-primary border-primary/40 hover:bg-primary/5 hover:border-primary"
              onClick={handleOpenCreate}
              disabled={disabled}
            >
              <Bot className="h-4 w-4" />
            </Button>
          </SimpleTooltip>
        )}
      </div>

      {/* Recording Modal */}
      <RecordingTtsModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSaved={handleRecordingSaved}
        editRecordingId={editRecordingId}
      />
    </>
  );
}
